/**
 * Task #1435 — modal globale che intercetta i 403 di limite tenant emessi
 * dall'apiClient via evento `api:tenant-limit-reached` e spiega all'utente
 * cosa fare (contattare il provider per estendere il piano).
 *
 * Da montare UNA volta in App.tsx: ogni tentativo fallito di creare utente o
 * agente AI con codice `tenant_user_limit_reached` / `tenant_ai_agent_limit_reached`
 * apre automaticamente questo modal — i singoli call site non devono replicare
 * la logica.
 */
import { useEffect, useState } from 'react';
import { AlertOctagon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { TENANT_LIMIT_EVENT, type TenantLimitEventDetail, type TenantLimitErrorCode } from '@/lib/tenantLimits';

const TITLE_BY_CODE: Record<TenantLimitErrorCode, string> = {
  tenant_user_limit_reached: 'Limite utenti raggiunto',
  tenant_ai_agent_limit_reached: 'Limite agenti AI raggiunto',
};

const RESOURCE_BY_CODE: Record<TenantLimitErrorCode, string> = {
  tenant_user_limit_reached: 'utenti',
  tenant_ai_agent_limit_reached: 'agenti AI',
};

export function TenantLimitErrorModal(): JSX.Element {
  const [detail, setDetail] = useState<TenantLimitEventDetail | null>(null);

  useEffect(() => {
    const handler = (ev: Event): void => {
      const ce = ev as CustomEvent<TenantLimitEventDetail>;
      if (ce.detail) setDetail(ce.detail);
    };
    window.addEventListener(TENANT_LIMIT_EVENT, handler);
    return () => window.removeEventListener(TENANT_LIMIT_EVENT, handler);
  }, []);

  const open = detail !== null;
  const close = (): void => setDetail(null);

  if (!detail) {
    return (
      <Dialog open={false} onOpenChange={close}>
        <DialogContent />
      </Dialog>
    );
  }

  const resource = RESOURCE_BY_CODE[detail.code];
  const title = TITLE_BY_CODE[detail.code];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/15 text-red-600 dark:text-red-300">
              <AlertOctagon className="h-5 w-5" />
            </div>
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            Hai esaurito gli {resource} inclusi nel piano del tenant
            {detail.limit > 0 ? ` (${detail.current}/${detail.limit} utilizzati)` : ''}.
            Per crearne altri è necessario contattare il provider e richiedere
            un'estensione del limite.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Ho capito</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default TenantLimitErrorModal;
